import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import { AuthorPayload } from "@pages/Author/utils/types";
import BreakingNews from "@pages/Section/components/BreakingNews/BreakingNews";
import { getAuthorJSONLD } from "@util/helpers";

import AuthorArticles from "./components/AuthorArticles";
import AuthorDetails from "./components/AuthorDetails";

export type AuthorPageContentProps = Pick<AuthorPayload, "data">;

/** The content for the author pages. */
const AuthorPageContent = ({
  data: { title, profileIds, profileDetails, articles, seoFooterData },
}: AuthorPageContentProps): React.ReactElement => {
  const breakingNews = useTrimmedBreakingNews();
  const { name, bio_raw, urlPath, headshotImage } = profileDetails;

  return (
    <>
      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish"]}
        path="/authors"
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData title={name} level2="authors" />

      <MetaTags
        title={title}
        description={bio_raw}
        ogType="article"
        slug={urlPath}
        jsonLd={getAuthorJSONLD(profileDetails)}
        imageUrl={headshotImage}
      />

      <div className="w-full" data-testid="author-page-content">
        <Ad
          adType="lb1"
          className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
        />

        <Header />

        <PulseEntry />

        <Container rootClassName="py-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <AuthorDetails {...profileDetails} />

              <AuthorArticles cardProps={articles} profileIds={profileIds} />
            </div>

            <div>
              <div className="mb-6 lg:h-[800px]">
                <Ad
                  adType="imu2"
                  className="flex flex-col items-center justify-center lg:!sticky lg:top-18"
                />
              </div>

              <BreakingNews
                data={breakingNews}
                rootClassName="max-w-[300px] mx-auto mb-6"
              />
            </div>
          </div>
        </Container>
      </div>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </>
  );
};

export default AuthorPageContent;
